import React, { FC, ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import colorVariables from '../../Globals/colors.module.css'
import fontVariables from '../../Globals/fonts.module.css'
import UkraineMap from 'components/UkraineMap'
import ContentGrid from 'components/ContentGrid'
import Column from 'components/Column'
import { useEffectOnce, useWindowSize } from 'usehooks-ts'
import { Scroller } from 'components/Scroller'
import { MicroFrontendProps } from 'Types/MicroFrontendProps'
import { Video } from 'components/Video'
import PowerPlantsLegend from 'components/PowerPlantsLegend'
import { EnergyAttacks } from 'Types/EnergyAttacks'

const bombs = require('../GeoJSON/EnergyAttacks.json') as EnergyAttacks
const colorPalette = {
  Gas: {
    light: 'var(--green-dark-1)',
    dark: 'var(--green-bright-1)',
  },
  Coal: {
    light: 'var(--orange-dark-1)',
    dark: 'var(--orange-dark-1)',
  },
  Nuclear: {
    light: 'var(--orange-main)',
    dark: 'var(--orange-bright-3)',
  },
  Hydro: {
    light: 'var(--petrol-main)',
    dark: 'var(--petrol-bright-1)',
  },
  'Onshore wind': {
    light: 'var(--purple-main)',
    dark: 'var(--purple-bright-1)',
  },
  'Solar PV': {
    light: '#f4e400',
    dark: 'var(--orange-bright-2)',
  },
  Oil: {
    light: 'var(--orange-bright-3)',
    dark: '#ffbf00',
  },
  Biopower: {
    light: 'var(--orange-bright-1)',
    dark: 'var(--orange-bright-2)',
  },
  bomb: {
    light: 'var(--orange-main)',
    dark: 'var(--orange-main)',
  },
  bomb_gas: {
    light: 'var(--green-dark-1)',
    dark: 'var(--green-bright-1)',
  },
  bomb_water: {
    light: 'var(--blue-dark-1)',
    dark: 'var(--blue-bright-1)',
  },
  bomb_electricity: {
    light: 'var(--orange-main)',
    dark: 'var(--orange-main)',
  },
} as {
  [key: string]: { light: string; dark: string }
}
const getCoordsOfBomb = (bombId: number): [number, number] | undefined =>
  bombs?.features?.find((b) => b.properties.id === bombId)?.geometry
    ?.coordinates

interface Phase {
  title: string
  highlights?: RegionLabelType[]
  pointOfInterestJson?: string
  ruControlledJson: string
  frontline: string
  startTime: number
  endTime: number
}

const phases = [
  // PHASE 1
  {
    title: 'Februar – März',
    startTime: 1645484401,
    endTime: 1648764001,
    ruControlledJson: '2022-03-31-ru-controlled.json',
    frontline: '31. März',
    highlights: [
      {
        label: 'Elektrizitäts- und Gaskraftwerk, Kiew',
        pos: getCoordsOfBomb(22398172), // Gaskraftwerk
        r: [3, 3],
        offset: 'top',
        hideLine: true,
      },
    ],
  },
  {
    title: 'Februar – März',
    startTime: 1645484401,
    endTime: 1648764001,
    ruControlledJson: '2022-03-31-ru-controlled.json',
    frontline: '31. März',
    highlights: [
      {
        label: 'Damm, Nowa Kachowka',
        pos: getCoordsOfBomb(22398003),
        r: [2, 2],
        offset: 'top',
        hideLine: true,
      },
    ],
  },
  {
    title: 'Februar – März',
    startTime: 1645484401,
    endTime: 1648764001,
    ruControlledJson: '2022-03-31-ru-controlled.json',
    frontline: '31. März',
    highlights: [
      {
        label: 'Atomkraftwerk Saporischschja',
        pos: getCoordsOfBomb(22401742),
        r: [2, 2],
        offset: 'top',
        hideLine: true,
      },
    ],
  },
  // PHASE 2
  {
    title: 'April - August',
    startTime: 1648764000,
    endTime: 1661983200,
    ruControlledJson: '2022-08-31-ru-controlled.json',
    frontline: '31. August',
    highlights: [
      {
        label: 'Derhatschi',
        pos: getCoordsOfBomb(22419837),
        r: [2, 2],
        offset: 'top',
        hideLine: true,
      },
      {
        label: 'Donetsk',
        pos: getCoordsOfBomb(22423412),
        r: [2, 2],
        offset: 'top',
        hideLine: true,
      },
      {
        pos: [24.61006, 49.92736], //getCoordsOfBomb(22432027),
        r: [2, 2],
        offset: 'top',
        hideLine: true,
      },
      {
        pos: getCoordsOfBomb(22436276),
        r: [2, 2],
        offset: 'bottom',
      },
      {
        label: 'Lemberg',
        pos: [24.3006, 49.92736], //getCoordsOfBomb(22432027),

        offset: 'top',
        hideLine: true,
      },
      {
        label: 'Selenodolsk',
        pos: getCoordsOfBomb(22433032),
        r: [2, 2],
        offset: 'top',
        hideLine: true,
      },
    ],
  },
  {
    title: 'April - August',
    startTime: 1648764000,
    endTime: 1661983200,
    ruControlledJson: '2022-08-31-ru-controlled.json',
    frontline: '31. August',
    highlights: [
      {
        label: 'Luhansk',
        pos: getCoordsOfBomb(22446935),
        r: [2, 2],
        offset: 'bottom',
        hideLine: true,
      },
      {
        label: 'Tschuhujiw',
        pos: getCoordsOfBomb(22468106),
        r: [2, 2],
        offset: 'top',
        hideLine: true,
      },
    ],
  },
  {
    title: 'April - August',
    startTime: 1648764000,
    endTime: 1661983200,
    ruControlledJson: '2022-08-31-ru-controlled.json',
    frontline: '31. August',
    highlights: [
      {
        label: 'AKW Saporischschja',
        pos: getCoordsOfBomb(22474765),
        r: [2, 2],
        offset: 'top',
        hideLine: true,
      },
      // {
      //   pos: getCoordsOfBomb(22485540),
      //   r: [2, 2],
      //   offset: 'top',
      //   hideLine: true,
      // },
    ],
  },
  // PHASE 3
  {
    title: 'September - Oktober',
    startTime: 1661983201,
    endTime: 1667400089,
    ruControlledJson: '2022-10-31-ru-controlled.json',
    frontline: '31. Oktober',
    pointOfInterestJson: require('../GeoJSON/country.json'),

    highlights: [
      {
        label: 'Saporischschja',
        pos: getCoordsOfBomb(22497864),
        r: [2, 2],
        offset: 'bottom',
      },
      {
        label: 'Schitomir',
        pos: getCoordsOfBomb(22498520),
        r: [2, 2],
        offset: 'top',
      },
      {
        label: 'Poltawa',
        pos: getCoordsOfBomb(22498530),
        r: [2, 2],
        offset: 'top',
      },
      {
        label: 'Lemberg',
        pos: getCoordsOfBomb(22498562),
        r: [2.5, 2.5],
        offset: 'top',
      },
      {
        label: 'Chmelnyzkyj',
        pos: getCoordsOfBomb(22498607),
        r: [2, 2],
        offset: 'bottom',
      },
      {
        label: 'Wilnohirsk',
        pos: getCoordsOfBomb(22499422),
        r: [2, 2],
        offset: 'right',
      },
      {
        label: 'Charkiw',
        pos: getCoordsOfBomb(22498617),
        r: [2, 2],
        offset: 'right',
      },
      {
        label: 'Kiew',
        pos: getCoordsOfBomb(22498676),
        r: [2, 2],
        offset: 'bottom',
      },
    ],
  },
  {
    title: 'September - Oktober',
    startTime: 1661983201,
    endTime: 1667400089,
    ruControlledJson: '2022-10-31-ru-controlled.json',
    frontline: '31. Oktober',
    highlights: [
      {
        label: 'Schitomir',
        pos: getCoordsOfBomb(22501481),
        r: [2, 2],
        offset: 'bottom',
        hideLine: true,
      },
      {
        label: 'Kiew',
        pos: getCoordsOfBomb(22501602),
        r: [2, 2],
        offset: 'top',
        hideLine: true,
      },
      {
        label: 'Charkiw',
        pos: getCoordsOfBomb(22501160),
        r: [2, 2],
        offset: 'top',
        hideLine: true,
      },
    ],
  },
  {
    title: 'September - Oktober',
    startTime: 1661983201,
    endTime: 1667400089,
    ruControlledJson: '2022-10-31-ru-controlled.json',
    frontline: '31. Oktober',
    pointOfInterestJson: require('../GeoJSON/country.json'),
  },
  {
    startTime: 1661983201,
    endTime: 0,
    ruControlledJson: '2022-10-31-ru-controlled.json',
    frontline: '31. Oktober',
    pointOfInterestJson: require('../GeoJSON/2022-10-31-ru-controlled.json'),
  },
] as Phase[]
const MainComponent: FC<MicroFrontendProps> = ({
  tenant,
  colorMode,
  properties = {
    time: 'february',
    title: 'Title from MF-Properties',
    sub: 'Mai',
  },
}): ReactElement => {
  const { width } = useWindowSize()
  const [height, setHeight] = useState(0)
  const [sizeFactor, setSizeFactor] = useState(0.0025)
  const [colorModeOverride, setColorModeOverride] = useState<
    'light' | 'dark' | null
  >(null)
  const globalClasses = [
    colorVariables.globalColors,
    fontVariables.globalFonts,
    colorMode === 'dark' || colorModeOverride === 'dark'
      ? colorVariables.globalColorsDark
      : '',
  ]

  useEffectOnce(() => {
    setHeight(window.innerHeight)
  })

  useEffect(() => {
    const newFactor = width < 700 ? 0.00325 : 0.009
    setSizeFactor(newFactor)
  }, [width])
  return (
    <AppContainer className={globalClasses.join(' ')}>
      {
        <ContentGrid>
          <Column wide>
            {/* <UkraineMapAnimated /> */}
            <Scroller
              defaultDistance={height}
              front={[
                <FrontElement>
                  Über 60 Angriffe auf die ukrainische Energieinfrastruktur sind
                  allein in der ersten Kriegsphase bis Ende März dokumentiert.
                  Das sind im Schnitt zwei pro Tag. In dieser Zeit versuchen die
                  Russen, mit einem «Enthauptungsschlag» die Ukraine
                  handstreichartig einzunehmen. Viele Angriffe konzentrieren
                  sich daher auf das Machtzentrum, den <b>Grossraum Kiew</b>.
                  Bereits am zweiten und dritten Tag des Kriegs gibt es Angriffe
                  auf ein
                  <b> Gas- </b>und ein <b>Elektrizitätswerk</b> in der Stadt.
                </FrontElement>,
                <FrontElement>
                  In den sozialen Medien kursieren Angriffspläne, die zeigen,
                  dass es von allem Anfang Teil der russischen Strategie ist,
                  die wichtige Infrastruktur in der Ukraine zu zerstören.
                  <br />
                  Diese Angriffspläne auf den Damm des{' '}
                  <b>Nowa-Kachowka-Wasserkraftwerks</b> im Süden der Ukraine
                  werden am 25. Februar veröffentlicht:
                  <ImageInText
                    src={require('assets/AngriffsPlan.jpeg')}
                    alt='Angriffspläne auf den Damm des Nowa-Kachowka-Wasserkraftwerks'
                  />
                  <Source>
                    Quelle:{' '}
                    <a href='https://twitter.com/GirkinGirkin/status/1497180365043212290'>
                      Twitter
                    </a>
                  </Source>
                </FrontElement>,
                <FrontElement>
                  Und tatsächlich greifen die russischen Truppen bereits am 3.
                  März das <b>Atomkraftwerk Saporischschja</b> im Südosten der
                  Ukraine an – mit sechs Meilern das grösste Atomkraftwerk
                  Europas. Ein Unfall wäre zehnmal schlimmer als die
                  Reaktorkatastrophe von Tschernobyl, warnt der ukrainische
                  Präsident Wolodimir Selenski. «Das wäre das Ende Europas.»
                  <br />
                  <br />
                  Dieses Video zeigt die ersten Kämpfe bei der nuklearen Anlage,
                  aufgenommen vermutlich von einer Überwachungskamera:
                  <Video
                    filename='Saporischschja1'
                    source='https://twitter.com/BNONews/status/1499538547187105793'
                  ></Video>
                  Bereits einen Tag nach dem Angriff vermeldet die ukrainische
                  Seite die Besetzung des Kraftwerks. Die Russen dringen auf das
                  Gelände vor, wie hier zu sehen ist:
                  <Video
                    filename='Saporischschja2'
                    source='https://twitter.com/YWNReporter/status/1499693084237570052'
                  ></Video>
                </FrontElement>,
                <FrontElement>
                  Nach dem gescheiterten Enthauptungsschlag der Russen
                  verschiebt sich der Krieg in den Osten und in den Süden in der
                  Region Cherson. Als Folge nimmt die Kadenz der Angriffe auf
                  die kritische Energieinfrastruktur der Ukraine ab. <br />
                  <br />
                  In der Summe sind die Schäden trotzdem massiv: So brennen im
                  April innerhalb kurzer Zeit mindestens sechs{' '}
                  <b>Umspannwerke </b> oder
                  <b> Verteilstationen </b> nach russischem Raketen- oder
                  Artilleriebeschuss – in <b>Derhatschi</b>, in <b>Donezk</b>{' '}
                  und in <b>Lemberg. </b>
                  Gebietsweise kommt es zu Stromausfällen.
                  <br />
                  <br />
                  Auch in <b>Selenodolsk</b> zerstören die Russen ein{' '}
                  <b>Umspannwerk,</b> wie auf diesem Video zu sehen ist:
                  <Video
                    filename='Selenodolsk'
                    source='https://t.me/dnepr_operativ/14813'
                  ></Video>
                </FrontElement>,
                <FrontElement>
                  Regelmässig fügen die russischen Granaten, Raketen und Drohnen
                  auch der<b> Gas- </b>und<b> Wasserinfrastruktur </b>schwere
                  Schäden zu. Einige Treffer werden vermutlich gezielt sein,
                  andere wiederum sind bloss Kollateralschäden, weil die Russen
                  mit ihrer Artillerie so oder so ganze Städte in Schutt und
                  Asche legen.
                  <br />
                  <br />
                  Mindestens 20 Treffer auf die<b> Gasinfrastruktur </b>sind in
                  dieser Phase des Kriegs erfasst. In der Region<b> Luhansk </b>
                  fällt die Versorgung regelmässig aus. Im Mai zerstören die
                  Russen auch noch die letzte Gasverteilstation in der Region
                  Luhansk.
                  <br />
                  <br />
                  Und fast gleich oft trifft es die<b> Wasserversorgung. </b>
                  Immer wieder kommt es deswegen zu Rationierungen. In einigen
                  Städten fällt die Versorgung teilweise tagelang aus. Besonders
                  gravierend ist ein russischer Angriff auf eine Wasserleitung
                  in<b> Tschuhujiw </b>in der Region Charkiw.
                </FrontElement>,
                <FrontElement>
                  Das<b> Atomkraftwerk Saporischschja </b>bleibt weiter ein
                  Ziel. Die Russen verschleppen den Leiter der Anlage, lassen
                  ihn später aber wieder frei. Zudem deponieren sie Sprengstoff
                  bei den Reaktorblöcken, vermutlich, um der Ukraine
                  Gegenangriffe zu erschweren. Dieses Video zeigt die
                  «verdächtigen Aktivitäten» der russischen Truppen:
                  <Video
                    filename='Saporischschja3'
                    source='https://twitter.com/666_mancer/status/1555832939337883648'
                  ></Video>
                  Schliesslich zerstören die Russen auch die letzte Leitung des
                  Atomkraftwerks, die noch ins Netz der Ukraine einspeist. Damit
                  liefert die Anlage keinen Strom mehr. Zurzeit ist das Werk
                  heruntergefahren, es lief allerdings schon vor der russischen
                  Eroberung nicht mehr auf Volllast.
                  <br />
                  <br />
                  Die Gefahr eines Unfalls ist aber nicht gebannt, denn die
                  Reaktoren müssen gekühlt werden, und das braucht Energie. Auf
                  dem Dach eines Reaktorblocks haben die russischen Soldaten
                  gemäss der Zeitung «Kyiv Independent» mittlerweile Geräte zur
                  elektronischen Kampfführung installiert.
                </FrontElement>,
                <FrontElement>
                  In diese Phase des Kriegs fallen die erfolgreichen
                  Gegenoffensiven der Ukraine vor allem im Osten des Landes.
                  Innerhalb weniger Tage erobern die Ukrainer Anfang September
                  grosse Gebiete zurück.
                  <br />
                  <br />
                  Die Reaktion der Russen auf die Niederlagen ist heftig: Die
                  Angriffe auf die Energieinfrastruktur werden fortan zur
                  eigentlichen Kriegstaktik. Dabei geraten vermehrt
                  Umspannwerke, Verteilstationen und Stromleitungen ins Visier.
                  Der Grund: Diese Infrastruktur kann im Gegensatz zu
                  Kraftwerken kaum geschützt werden.
                  <br />
                  <br />
                  Die massivste Angriffswelle der Russen ereignet sich am 10.
                  und 11. Oktober. Besonders betroffen sind unter anderen die
                  Hauptstadt<b> Kiew </b>sowie die Städte
                  <b>
                    {' '}
                    Dnipro, Iwano-Frankiwsk, Charkiw, Chmelnizki, Lemberg,
                    Poltawa, Sumi, Wilnohirsk, Winnizja, Saporischschja und
                    Schitomir.{' '}
                  </b>
                  Innerhalb kurzer Zeit gibt es mindestens 50 Stromausfälle
                  landesweit.
                  <br />
                  <br />
                  In Lemberg werden 90 Prozent der Elektrizitätsinfrastruktur
                  zerstört. In den Strassen bleibt es nachts dunkel, wie auf
                  diesen Bildern vom 10. Oktober zu sehen ist:
                  <ImageInText
                    src={require('assets/Blackout1.jpeg')}
                    alt='Blackout in der Westukraine'
                  />
                  <ImageInText
                    src={require('assets/Blackout2.jpeg')}
                    alt='Blackout in der Westukraine'
                  />
                  <ImageInText
                    src={require('assets/Blackout3.jpeg')}
                    alt='Blackout in der Westukraine'
                  />
                  <Source>
                    Quelle:{' '}
                    <a href='https://twitter.com/zwinkersmile/status/1579571741764235264/photo/2'>
                      Twitter
                    </a>
                  </Source>
                </FrontElement>,
                <FrontElement>
                  Am 17. und 18. Oktober folgt die nächste Angriffswelle der
                  Russen. Wieder verbreiten die Angriffe Angst und Schrecken,
                  auch wenn sie etwas weniger heftig ausfallen. In<b> Kiew </b>
                  sterben zwei Menschen, in<b> Charkiw </b>bleibt die U-Bahn
                  stehen, und in
                  <b> Schitomir </b>gibt es zeitweise keinen Strom.
                  <br />
                  <br />
                  Wie ausgeliefert die Ukrainer den Luftangriffen sind, zeigt
                  dieses Video vom 17. Oktober. Um eine Drohne vom Himmel zu
                  holen, müssen Polizisten ihre Gewehre benutzen:
                  <Video
                    filename='Drohne'
                    source='https://twitter.com/Gerashchenko_en/status/1581959594112262144'
                  ></Video>
                </FrontElement>,
                <FrontElement>
                  Mit den Angriffen auf die Energieinfrastruktur verfolgt
                  Russland mehrere Ziele: Zum einen sollen die Wirtschaft und
                  insbesondere die Kriegslogistik getroffen werden. Zum anderen
                  zwingt laut Militärbeobachtern die russische Strategie das
                  ukrainische Militär dazu, die Flugabwehr von den Frontlinien
                  zu den Städten zu verschieben.
                  <br />
                  <br />
                  Zentral sei aber auch der «psychologische Druck» gegenüber der
                  Zivilbevölkerung, schreibt der ukrainische Energiespezialist
                  Andrian Prokip in einer Analyse des Wilson Center, eines
                  US-Forschungsinstituts. So tragen die jüngsten Angriffe die
                  Handschrift des neuen russischen Oberbefehlshabers Sergei
                  Surowikin. Er ist aus früheren Kriegen bekannt dafür, keine
                  Rücksicht auf die Zivilbevölkerung zu nehmen. Er ist auch
                  berüchtigt als «Schlächter von Syrien».
                </FrontElement>,
                <FrontElement>
                  <SubTitle>
                    Viele Kohle- und Gaskraftwerke im umkämpften Gebiet
                  </SubTitle>
                  Bislang ist die Strategie der Russen erfolgreich. Vor dem
                  Krieg verfügte die Ukraine gemäss der Analyse des Wilson
                  Center über eine Kapazität zur Stromerzeugung von 56 Gigawatt.
                  Per Ende September waren 9 Gigawatt verloren, weil sie von
                  Anlagen stammen, die mittlerweile von den Russen besetzt sind.
                  Und davon stammen allein 6 Gigawatt vom heruntergefahrenen
                  Atomkraftwerk Saporischschja. Zudem sind 90 Prozent aller
                  Windkraft- und 30 Prozent aller Solaranlagen entweder zerstört
                  oder sie liegen in mittlerweile besetztem Gebiet. Letztere
                  tragen gemeinsam rund fünf Prozent zur gesamten
                  Stromproduktion bei.
                </FrontElement>,
              ]}
              back={(index) => {
                console.log({ index })
                setColorModeOverride(null)
                if (index === 6) {
                  setColorModeOverride('dark')
                }
                const powerPlants =
                  index >= 9
                    ? require('../GeoJSON/ukr_power_plant_Fromatted.json')
                    : undefined
                const currentPhase = phases[Math.max(index, 0)]
                return (
                  <>
                    <Column style={{ height: height + 'px' }}>
                      <MapWrapper className='MapWrapper'>
                        <Time>
                          {/* {index} */}
                          <OutlineSvg>
                            <text
                              x='50%'
                              y='50%'
                              textAnchor='middle'
                              dominantBaseline='middle'
                              className='background'
                            >
                              {currentPhase?.title}
                            </text>
                            <text
                              x='50%'
                              y='50%'
                              textAnchor='middle'
                              dominantBaseline='middle'
                              className='foreground'
                            >
                              {currentPhase?.title}
                            </text>
                          </OutlineSvg>
                        </Time>

                        <UkraineMap
                          sizeFactor={sizeFactor}
                          height={height}
                          zoomToRegionLabels={
                            true
                            // index >= 1 && index < 7
                          }
                          pointOfInterestJson={
                            currentPhase?.pointOfInterestJson
                          }
                          powerPlants={powerPlants}
                          startTimestamp={currentPhase?.startTime}
                          data={
                            {
                              features: bombs?.features?.filter((feat) => {
                                return (
                                  feat.properties?.timestamp &&
                                  // &&
                                  // +feat.properties?.timestamp >=
                                  //   currentPhase?.startTime
                                  +feat.properties?.timestamp <=
                                    currentPhase?.endTime
                                )
                              }),
                            } as EnergyAttacks
                          }
                          russianControl={
                            // index !== 16 &&
                            // require('../GeoJSON/' + Controlled[time])
                            currentPhase
                              ? require('../GeoJSON/' +
                                  currentPhase.ruControlledJson)
                              : require('../GeoJSON/' +
                                  '2022-10-31-ru-controlled.json')
                          }
                          // russianAdvance={
                          //   index >= 7 && require('../GeoJSON/Ruekschlag.json')
                          // }
                          regionLabels={currentPhase?.highlights}
                          colorMode={colorMode ?? 'light'}
                          index={index}
                          colorPalette={colorPalette}
                        />

                        <KeyWrapper
                          className={
                            index > 0 && index <= 9 ? 'active' : 'hidden'
                          }
                        >
                          {index <= 8 && (
                            <>
                              <LegendTitle>Infrastruktur:</LegendTitle>
                              <ColorLegend>
                                <ColorCircle
                                  color={colorPalette.bomb_gas[colorMode]}
                                ></ColorCircle>
                                Gas
                              </ColorLegend>
                              <ColorLegend>
                                <ColorCircle
                                  color={colorPalette.bomb_water[colorMode]}
                                ></ColorCircle>
                                Wasser
                              </ColorLegend>
                              <ColorLegend>
                                <ColorCircle
                                  color={
                                    colorPalette.bomb_electricity[colorMode]
                                  }
                                ></ColorCircle>
                                Strom
                              </ColorLegend>
                            </>
                          )}
                          {index >= 9 && (
                            <>
                              <SizeLegend>
                                {/* <svg
                                width='30'
                                height='30'
                                viewBox='0 0 50 50'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                                >
                                <circle
                                  cx='25'
                                  cy='25'
                                  r='24.5'
                                  stroke='black'
                                  />
                                <path
                                  d='M9 8.75C8.86193 8.75 8.75 8.86193 8.75 9V11.25C8.75 11.3881 8.86193 11.5 9 11.5C9.13807 11.5 9.25 11.3881 9.25 11.25V9.25H11.25C11.3881 9.25 11.5 9.13807 11.5 9C11.5 8.86193 11.3881 8.75 11.25 8.75H9ZM40.8198 41.0698C40.9579 41.0698 41.0698 40.9579 41.0698 40.8198L41.0698 38.5698C41.0698 38.4317 40.9579 38.3198 40.8198 38.3198C40.6817 38.3198 40.5698 38.4317 40.5698 38.5698L40.5698 40.5698L38.5698 40.5698C38.4317 40.5698 38.3198 40.6817 38.3198 40.8198C38.3198 40.9579 38.4317 41.0698 38.5698 41.0698L40.8198 41.0698ZM8.82322 9.17678L9.35355 9.70711L9.70711 9.35355L9.17678 8.82322L8.82322 9.17678ZM10.4142 10.7678L11.4749 11.8284L11.8284 11.4749L10.7678 10.4142L10.4142 10.7678ZM12.5355 12.8891L13.5962 13.9497L13.9497 13.5962L12.8891 12.5355L12.5355 12.8891ZM14.6569 15.0104L15.7175 16.0711L16.0711 15.7175L15.0104 14.6569L14.6569 15.0104ZM16.7782 17.1317L17.8388 18.1924L18.1924 17.8388L17.1317 16.7782L16.7782 17.1317ZM18.8995 19.253L19.9602 20.3137L20.3137 19.9602L19.253 18.8995L18.8995 19.253ZM21.0208 21.3744L22.0815 22.435L22.435 22.0815L21.3744 21.0208L21.0208 21.3744ZM23.1421 23.4957L24.2028 24.5563L24.5563 24.2028L23.4957 23.1421L23.1421 23.4957ZM25.2635 25.617L26.3241 26.6777L26.6777 26.3241L25.617 25.2635L25.2635 25.617ZM27.3848 27.7383L28.4454 28.799L28.799 28.4454L27.7383 27.3848L27.3848 27.7383ZM29.5061 29.8596L30.5668 30.9203L30.9203 30.5668L29.8596 29.5061L29.5061 29.8596ZM31.6274 31.981L32.6881 33.0416L33.0416 32.6881L31.981 31.6274L31.6274 31.981ZM33.7487 34.1023L34.8094 35.163L35.163 34.8094L34.1023 33.7487L33.7487 34.1023ZM35.8701 36.2236L36.9307 37.2843L37.2843 36.9307L36.2236 35.8701L35.8701 36.2236ZM37.9914 38.3449L39.052 39.4056L39.4056 39.052L38.3449 37.9914L37.9914 38.3449ZM40.1127 40.4663L40.643 40.9966L40.9966 40.643L40.4663 40.1127L40.1127 40.4663ZM9 8.5C8.72386 8.5 8.5 8.72386 8.5 9V13.5C8.5 13.7761 8.72386 14 9 14C9.27614 14 9.5 13.7761 9.5 13.5V9.5H13.5C13.7761 9.5 14 9.27614 14 9C14 8.72386 13.7761 8.5 13.5 8.5H9ZM40.8198 41.3198C41.0959 41.3198 41.3198 41.0959 41.3198 40.8198L41.3198 36.3198C41.3198 36.0437 41.0959 35.8198 40.8198 35.8198C40.5437 35.8198 40.3198 36.0437 40.3198 36.3198V40.3198H36.3198C36.0437 40.3198 35.8198 40.5437 35.8198 40.8198C35.8198 41.0959 36.0437 41.3198 36.3198 41.3198L40.8198 41.3198ZM8.64645 9.35355L9.17678 9.88388L9.88388 9.17678L9.35355 8.64645L8.64645 9.35355ZM10.2374 10.9445L11.2981 12.0052L12.0052 11.2981L10.9445 10.2374L10.2374 10.9445ZM12.3588 13.0659L13.4194 14.1265L14.1265 13.4194L13.0659 12.3588L12.3588 13.0659ZM14.4801 15.1872L15.5407 16.2478L16.2478 15.5407L15.1872 14.4801L14.4801 15.1872ZM16.6014 17.3085L17.6621 18.3692L18.3692 17.6621L17.3085 16.6014L16.6014 17.3085ZM18.7227 19.4298L19.7834 20.4905L20.4905 19.7834L19.4298 18.7227L18.7227 19.4298ZM20.844 21.5511L21.9047 22.6118L22.6118 21.9047L21.5511 20.844L20.844 21.5511ZM22.9654 23.6725L24.026 24.7331L24.7331 24.026L23.6725 22.9654L22.9654 23.6725ZM25.0867 25.7938L26.1473 26.8544L26.8544 26.1473L25.7938 25.0867L25.0867 25.7938ZM27.208 27.9151L28.2687 28.9758L28.9758 28.2687L27.9151 27.208L27.208 27.9151ZM29.3293 30.0364L30.39 31.0971L31.0971 30.39L30.0364 29.3293L29.3293 30.0364ZM31.4506 32.1577L32.5113 33.2184L33.2184 32.5113L32.1577 31.4506L31.4506 32.1577ZM33.572 34.2791L34.6326 35.3397L35.3397 34.6326L34.2791 33.572L33.572 34.2791ZM35.6933 36.4004L36.7539 37.461L37.461 36.7539L36.4004 35.6933L35.6933 36.4004ZM37.8146 38.5217L38.8753 39.5824L39.5824 38.8753L38.5217 37.8146L37.8146 38.5217ZM39.9359 40.643L40.4663 41.1734L41.1734 40.4663L40.643 39.9359L39.9359 40.643Z'
                                  fill='black'
                                  />
                              </svg> */}
                              </SizeLegend>
                              <LegendTitle>
                                Produktionskapazität (Grösse der Kreise)
                              </LegendTitle>

                              <ColorLegend>
                                <ColorCircle
                                  color={colorPalette.bomb_gas[colorMode]}
                                ></ColorCircle>
                                Gaskraft
                              </ColorLegend>
                              <ColorLegend>
                                <ColorCircle
                                  color={colorPalette['Coal'][colorMode]}
                                ></ColorCircle>
                                Kohlekraft
                              </ColorLegend>
                            </>
                          )}
                          <Source>
                            {currentPhase?.frontline && (
                              <>Frontverlauf vom {currentPhase?.frontline}</>
                            )}
                            <br />
                            Quelle:{' '}
                            {index < 9 && (
                              <>
                                <a
                                  href='https://acleddata.com/ukraine-crisis/'
                                  target={width < 712 ? '_self' : '_blank'}
                                >
                                  ACLED
                                </a>
                                ,{' '}
                                <a
                                  href='https://liveuamap.com/'
                                  target={width < 712 ? '_self' : '_blank'}
                                >
                                  Liveuamap
                                </a>
                                ,{' '}
                              </>
                            )}
                            <a
                              href='https://www.understandingwar.org/'
                              target={width < 712 ? '_self' : '_blank'}
                            >
                              ISW
                            </a>
                            {index >= 9 && (
                              <>
                                ,{' '}
                                <a
                                  href='https://www.globaldata.com/'
                                  target={width < 712 ? '_self' : '_blank'}
                                >
                                  GlobalData
                                </a>
                              </>
                            )}
                          </Source>
                        </KeyWrapper>
                      </MapWrapper>
                    </Column>
                  </>
                )
              }}
            />
          </Column>
        </ContentGrid>
      }
    </AppContainer>
  )
}
const SubTitle = styled.h3`
  font-family: var(--font-plex);
  margin: 0;
  margin-bottom: 1em;
`
const LegendTitle = styled.div`
  font-size: 1.5em;
  font-weight: bold;
  margin-bottom: 0.5em;
  color: var(--text-color);
`
const SizeLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-size: 1.5em;
  transition: color 1s ease-in-out;
`
const ColorLegend = styled.div`
  font-size: 1.5em;
  padding-bottom: 0.4em;
  color: var(--text-color);
  transition: color 1s ease-in-out;
`
const ColorCircle = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  background-color: ${({ color }) => color};
  border-radius: 50%;
  display: inline-block;
  margin-right: 5px;
`
const FrontElement = styled.div<{ distance?: number }>`
  text-align: left;
  background-color: var(--site-background);
  color: var(--text-color);
  font-size: 18px;
  padding: 30px;
  border-radius: 2px;
  max-width: 80%;
  margin-left: auto;
  margin-right: auto;
  font-family: var(--font-noto);
  line-height: 3rem;
  max-width: 606px;
  -webkit-box-shadow: 0px 0px 9px -1px rgba(0, 0, 0, 0.3),
    0px 0px 9px -1px rgba(255, 255, 255, 0.3);
  box-shadow: 0px 0px 9px -1px rgba(0, 0, 0, 0.3),
    0px 0px 9px -1px rgba(255, 255, 255, 0.3);

  &.high {
    margin-top: 300px;
  }
  @media only screen and (max-width: 711px) {
    max-width: calc(100% + 1rem);
    width: calc(100% + 1rem);
    padding-right: 2rem;
    margin-left: -2rem;
    padding-left: 15px;
    padding-right: 15px;
  }
`

const Title = styled.div`
  font-family: var(--font-noto);
  font-size: 26px;
  color: var(--text-color);
  font-weight: 700;
  text-align: center;
  padding-top: 30px;
  padding-bottom: 10px;
`

const KeyWrapper = styled.div`
  &.active {
    opacity: 1;
  }
  opacity: 0;
  transition: all 0.5s ease-in-out;
  max-width: 666px;

  width: 100%;
  margin-left: auto;
  margin-right: auto;
  position: fixed;
  box-sizing: border-box;
  bottom: 0;
  padding-bottom: 2em;
  padding-left: 1em;
`
const AttackCounter = ({
  air,
  artillery,
  colorMode,
}: {
  air: number
  artillery: number
  colorMode: 'light' | 'dark'
}) => {
  return (
    <StyledAttackCounter>
      <AttackElement>
        <div className='icon'>
          <img
            src={require(colorMode === 'light'
              ? '../../assets/icons_artillery.png'
              : '../../assets/icons_artillery_dm.png')}
          />
        </div>
        <div className='value'> {artillery}</div>
        <div className='label'>Artillerie-, Raketenangriffe</div>
      </AttackElement>
      <AttackElement>
        <div className='icon'>
          <img
            src={require(colorMode === 'light'
              ? '../../assets/icons_air.png'
              : '../../assets/icons_air_dm.png')}
          />
        </div>
        <div className='value'>{air}</div>
        <div className='label'>Luftangriffe</div>
      </AttackElement>{' '}
    </StyledAttackCounter>
  )
}

const ImageInText = styled.img`
  width: 100%;
  margin-top: 1em;
`

const StyledAttackCounter = styled.div`
  width: 70%;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  font-family: var(--font-plex);
  margin-bottom: 2em;
  color: var(--text-color);
  border-top: 1px solid #c8c8d1;
  padding-top: 2em;
  @media only screen and (max-width: 711px) {
    width: 100%;
  }
`
const StyledBuildingCounter = styled.div`
  width: 70%;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-wrap: wrap;
  font-family: var(--font-plex);
  margin-bottom: 2em;
  color: var(--text-color);
  padding-top: 2em;
  @media only screen and (max-width: 711px) {
    width: 100%;
  }
`
const BuildingText = styled.div`
  flex: 0 0 100%;
  text-align: center;
  &.top {
    padding-bottom: 30px;
  }
  &.bottom {
    padding-top: 30px;
  }
`
const AttackElement = styled.div`
  flex: 0 0 50%;
  text-align: center;
  .icon {
    line-height: 0;
    img {
      max-width: 80px;
    }
  }
  .value {
    font-size: 30px;
    font-weight: 700;
    line-height: 1.5em;
  }
  .label {
    font-size: 15px;
    line-height: 1.2em;
  }
`
const BuildingElement = styled.div`
  flex: 0 0 33%;
  text-align: center;
  .icon {
    line-height: 0;
    img {
      max-width: 100px;
      margin-bottom: 0.25em;
      @media only screen and (max-width: 711px) {
        max-width: 80px;
      }
    }
  }
  .value {
    font-size: 30px;
    font-weight: 700;
    line-height: 1.5em;
  }
  .label {
    line-height: 1.2em;
    @media only screen and (max-width: 711px) {
      font-size: 15px;
    }
  }
`
const OrangeMarker = styled.div<{ colorMode: 'light' | 'dark' }>`
  display: inline-block;
  width: 0.7em;
  height: 0.75em;
  border-radius: 0.75em;
  margin-left: 0.1em;
  margin-right: 0.1em;
  background-color: ${({ colorMode }) =>
    colorMode === 'light' ? '#e16d00' : '#e16d00'};
`
const BlueMarker = styled.div<{ colorMode: 'light' | 'dark' }>`
  display: inline-block;
  width: 0.75em;
  height: 0.75em;
  background-color: ${({ colorMode }) =>
    colorMode === 'light' ? '#202346' : '#69aacd'};
  border-radius: 0.75em;
  margin-left: 0.1em;
  margin-right: 0.1em;
`
const OutlineSvg = styled.svg`
  font-family: var(--font-plex);

  text {
    font-weight: 300;
  }
  .foreground {
    fill: var(--text-color);
  }
  .background {
    stroke-width: 4px;
    stroke-linejoin: round;
    animation: 2s pulsate infinite;
    fill: none;
    stroke: var(--site-background);
  }
`
const Time = styled.div`
  position: absolute;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  font-size: 26px;
  color: var(--text-color);
  font-weight: 300;
  width: 100%;
  text-align: center;
  min-height: 3em;
  @media only screen and (max-width: 711px) {
    font-size: 24px;
    padding-top: 30px;
  }
`
const TimeTitle = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  font-size: 22px;
  color: var(--text-color);
  font-weight: 300;
  padding-bottom: 1em;
  font-family: var(--font-plex);

  width: 100%;
  text-align: center;
  flex: 0 0 10%;
  @media only screen and (max-width: 711px) {
    font-size: 20px;
  }
`
const Source = styled.div`
  font-family: var(--font-plex);
  font-size: 12px;
  color: var(--gray-3);
  a {
    color: var(--blue-main);
    text-decoration: none;
  }
`
const MapWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 2em;
  @media only screen and (max-width: 711px) {
    margin-top: 0;

    /* width: 118%;
    margin-left: -9%; */
    max-width: 100vw;
    overflow: hidden;
    margin-left: -2rem;
    max-width: calc(100% + 4rem);
    width: calc(100% + 4rem);
  }
`
const AppContainer = styled.div<{
  tenant?: string
}>`
  width: 100%;
  transition: background 2s ease;

  margin: 0;
  margin-top: 60px;
  margin-bottom: 60px;
  padding: 0;

  font-family: var(--font-plex);
  color: var(--brandblue-main);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: var(--site-background);
  @media (prefers-reduced-motion: no-preference) {
    .App-logo {
      animation: App-logo-spin infinite 20s linear;
    }
  }

  @keyframes App-logo-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`
export default MainComponent
